import React, { useState, useRef, useEffect } from 'react';
import { Settings as SettingsIcon, Shield, X, Moon, Sun, Sliders, UserCog } from 'lucide-react';
import { Theme } from '../types';
import { getModelDisplayName } from '../utils/modelNames';

interface SettingsProps {
  theme: Theme;
  onThemeChange: (theme: Theme) => void;
  showInternalContent: boolean;
  onToggleInternalContent: () => void;
  isDev: boolean;
  goToAdminPage: () => void;
  onLogout: () => void;
  sendKey: string; 
  onSendKeyChange: (key: string) => void;
  modelSelection?: number; // Current model selection value
  maxModelOptions?: number; // Maximum number of models available
  onModelSelectionChange?: (model: number) => void; // Handler for model selection change
  ipProtection: boolean; // Whether IP protection is enabled
  onToggleIpProtection: () => void; // Handler for toggling IP protection
  modelList?: string[]; // Add modelList prop
}

export function Settings({ 
  theme, 
  onThemeChange, 
  showInternalContent, 
  onToggleInternalContent, 
  isDev, 
  goToAdminPage, 
  onLogout,
  sendKey, 
  onSendKeyChange,
  modelSelection = 0,
  maxModelOptions = 0,
  onModelSelectionChange,
  ipProtection = false,
  onToggleIpProtection,
  modelList = [] // Add modelList prop with default empty array
}: SettingsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'appearance' | 'behavior' | 'admin'>('appearance');
  const settingsRef = useRef<HTMLDivElement>(null);
  const resolveModelName = (index: number) =>
    getModelDisplayName(index, modelList[index]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={settingsRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
        aria-label="Settings"
        title="Settings"
      >
        <SettingsIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-850">
            <h3 className="font-medium text-gray-800 dark:text-white">Settings</h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-full p-1 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Tab Navigation */}
          <div className="flex border-b dark:border-gray-700">
            <button
              onClick={() => setActiveTab('appearance')}
              className={`flex-1 py-3 px-2 text-sm font-medium transition-colors ${
                activeTab === 'appearance' 
                  ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500' 
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'
              }`}
            >
              <div className="flex items-center justify-center gap-1.5">
                {theme === 'dark' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                <span>Appearance</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('behavior')}
              className={`flex-1 py-3 px-2 text-sm font-medium transition-colors ${
                activeTab === 'behavior' 
                  ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500' 
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'
              }`}
            >
              <div className="flex items-center justify-center gap-1.5">
                <Sliders className="w-4 h-4" />
                <span>Behavior</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('admin')}
              className={`flex-1 py-3 px-2 text-sm font-medium transition-colors ${
                activeTab === 'admin' 
                  ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500' 
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'
              }`}
            >
              <div className="flex items-center justify-center gap-1.5">
                <Shield className="w-4 h-4" />
                <span>Admin</span>
              </div>
            </button>
          </div>
          
          {/* Tab Content */}
          <div className="p-4">
            {activeTab === 'appearance' && (
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Theme</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => onThemeChange('light')}
                      className={`flex items-center justify-center gap-2 p-2.5 rounded-md transition-colors ${
                        theme === 'light'
                          ? 'bg-blue-100 border-blue-500 text-blue-600 border dark:bg-blue-900/30 dark:text-blue-400'
                          : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-650'
                      }`}
                    >
                      <Sun className="w-4 h-4" />
                      <span>Light</span>
                    </button>
                    <button
                      onClick={() => onThemeChange('dark')}
                      className={`flex items-center justify-center gap-2 p-2.5 rounded-md transition-colors ${
                        theme === 'dark'
                          ? 'bg-blue-100 border-blue-500 text-blue-600 border dark:bg-blue-900/30 dark:text-blue-400'
                          : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-650'
                      }`}
                    >
                      <Moon className="w-4 h-4" />
                      <span>Dark</span>
                    </button>
                  </div>
                </div>
                
                {isDev && (
                  <div>
                    <label className="flex items-center space-x-2 py-1.5 px-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={showInternalContent}
                        onChange={onToggleInternalContent}
                        className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Show Internal Content
                      </span>
                    </label>
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'behavior' && (
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Message Send Key</label>
                  <div className="flex flex-col gap-2">
                    <label className={`flex items-center gap-3 p-2.5 rounded-md border transition-colors cursor-pointer ${
                      sendKey === 'Enter'
                        ? 'bg-blue-50 border-blue-500 text-gray-800 dark:bg-blue-900/20 dark:text-gray-200 dark:border-blue-500/70'
                        : 'bg-white border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-650'
                    }`}>
                      <input
                        type="radio"
                        name="sendKey"
                        value="Enter"
                        checked={sendKey === 'Enter'}
                        onChange={() => onSendKeyChange('Enter')}
                        className="text-blue-500 focus:ring-blue-500 h-4 w-4"
                      />
                      <div>
                        <div className="text-sm font-medium">Enter to send</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Use Shift+Enter for new line</div>
                      </div>
                    </label>
                    
                    <label className={`flex items-center gap-3 p-2.5 rounded-md border transition-colors cursor-pointer ${
                      sendKey === 'CtrlEnter'
                        ? 'bg-blue-50 border-blue-500 text-gray-800 dark:bg-blue-900/20 dark:text-gray-200 dark:border-blue-500/70'
                        : 'bg-white border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-650'
                    }`}>
                      <input
                        type="radio"
                        name="sendKey"
                        value="CtrlEnter"
                        checked={sendKey === 'CtrlEnter'}
                        onChange={() => onSendKeyChange('CtrlEnter')}
                        className="text-blue-500 focus:ring-blue-500 h-4 w-4"
                      />
                      <div>
                        <div className="text-sm font-medium">Ctrl+Enter to send</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Use Enter for new line</div>
                      </div>
                    </label>
                  </div>
                </div>
                
                {isDev && (
                  <div>
                    <label className="flex items-center space-x-2 py-1.5 px-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={ipProtection}
                        onChange={onToggleIpProtection}
                        className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        IP Protection
                      </span>
                    </label>
                    <div className="text-xs text-gray-500 dark:text-gray-400 ml-6 mt-1">
                      Prevents the system from storing or using your IP address for improvement
                    </div>
                  </div>
                )}
                
                {maxModelOptions > 1 && onModelSelectionChange && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Model Selection
                    </label>
                    {isDev ? (
                      <select
                        value={modelSelection}
                        onChange={(e) => onModelSelectionChange(parseInt(e.target.value))}
                        className="mt-1 w-full px-3 py-2 text-sm border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        style={{ borderColor: '#ddd', focusRingColor: '#279DD9' }}
                      >
                        {modelList.map((model, index) => (
                          <option key={index} value={index}>
                            {model}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <>
                        <input
                          type="range"
                          min="0"
                          max={maxModelOptions - 1}
                          value={modelSelection}
                          onChange={(e) => onModelSelectionChange(parseInt(e.target.value))}
                          className="mt-1 w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                        />
                        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                          <span>{resolveModelName(0)}</span>
                          <span className="text-center text-gray-600 dark:text-gray-300">
                            {resolveModelName(modelSelection)} ({modelSelection + 1}/{maxModelOptions})
                          </span>
                          <span>{resolveModelName(maxModelOptions - 1)}</span>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'admin' && (
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setIsOpen(false);
                    goToAdminPage();
                  }}
                  className="flex items-center justify-between w-full p-3 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-750 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-700 transition-colors shadow-sm"
                >
                  <div className="flex items-center gap-2.5">
                    <UserCog className="w-5 h-5 text-blue-500 dark:text-blue-400" />
                    <span className="font-medium">Admin Dashboard</span>
                  </div>
                  <span className="text-gray-400">→</span>
                </button>
                
                {/* Removed logout button as it's now in the sidebar user profile */}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
